import type { BreakpointSettings, PlayerOptions, TranscriptCue } from './player.interface';
import type { QualityLevelList } from 'videojs-contrib-quality-levels';

import { default as detectMobile } from 'ismobilejs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VideoJsPlayer } from 'video.js';

import { useYouboraAnalytics } from './hooks/analytics/use-youbora-analytics';
import { useAudioTracks } from './hooks/use-audio-tracks';
import { useExternalCues } from './hooks/use-external-cues';
import { useQualityLevels } from './hooks/use-quality-levels';
import { useSetSource } from './hooks/use-set-source';
import { useSubtitles } from './hooks/use-subtitles';
import { useTimedMetadata } from './hooks/use-timed-metadata';
import { ProgressiveSources } from './player.config';
import { getInitialQualityId } from './quality.utils';
import { setPlayerSrc } from './utils/set-player-src';
import { tryToPlay } from './utils/try-to-play';
import { VideoJsNativeEvent } from './videojs-event';
import { PosterSource } from './videojs-poster';
import { useRefState } from '../../hooks/use-ref-state';
import { getPlaybackSpeeds } from '../../utils/get-playback-speeds';
import { isHLSSource } from '../../utils/is-hls-source';
import { sToMs } from '../../utils/time';
import { ErrorMessage } from '../error-message/error-message';
import { useMediaSession } from './hooks/use-media-session';
import { type AudioTrack } from '../language-menu/language-menu.types';
import { AutoQualityId, QualityLevelOption } from '../settings-menu/settings-menu.types';
import { Chapter } from '../timeline/timeline.types';
import { UserInterface } from '../user-interfaces/user-interface';
import { usePlayerReady } from './hooks/use-player-ready';
import { useVideoDataLoaded } from './hooks/use-video-data-loaded';
import { useLogger } from '../../contexts/logger-context';
import { getPackageVersion } from '../../utils/version';

interface Props {
  player: VideoJsPlayer;
  playerOptions: PlayerOptions;
}

export function PlayerUIConnector({ player, playerOptions }: Props): JSX.Element | null {
  const { logger } = useLogger();
  useEffect(() => {
    logger.info('Player version:', getPackageVersion());
  }, [logger]);

  const { t } = useTranslation();
  const { settings } = playerOptions;

  // TODO: Remove this when we have a better solution
  const isPhone = useMemo(() => {
    return process.env.STORYBOOK_BUILD
      ? detectMobile(window.sessionStorage.getItem('storyBookUserAgent') ?? '').phone
      : detectMobile(window.navigator).phone;
  }, []);

  // Set poster source
  const posterRef = useRef<PosterSource | undefined>(undefined);
  useEffect(() => {
    if (
      posterRef.current !== playerOptions.poster &&
      JSON.stringify(posterRef.current) !== JSON.stringify(playerOptions.poster)
    ) {
      player.miPoster?.setSrc(playerOptions.poster);
      posterRef.current = playerOptions.poster;
    }
  }, [player, playerOptions.poster]);

  // Set player layout and aspect ratio
  useEffect(() => {
    if (settings.videoLayout) {
      switch (settings.videoLayout) {
        case 'fill':
          player.fill(true);
          break;
        case 'fluid':
          player.fluid(true);
          if (settings.aspectRatio) {
            player.aspectRatio(settings.aspectRatio);
          }
          break;
        default:
          player.fluid(false);
          player.fill(false);
          break;
      }
    }
  }, [player, settings.aspectRatio, settings.videoLayout]);

  /* -- Analytics -- */
  useYouboraAnalytics(player, settings, playerOptions.analyticsMode, playerOptions.plugins.youboraAnalytics);

  /* -- Qualities -- */
  const [qualityId, setQualityId] = useState<string | undefined>(undefined);
  const qualities = useQualityLevels(player, playerOptions.sources.progressive);

  /* -- AudioTracks -- */
  const [audioTrackId, setAudioTrackId] = useState<string | undefined>(undefined);
  const audioTracks: AudioTrack[] = useAudioTracks(player);
  const [initialAudioTrackId, setInitialAudioTrackId] = useState('');

  const handleAudioTrackSelection = useCallback(
    (audioTrackId: string) => {
      const audioTrackList = player.audioTracks();
      if (audioTrackList) {
        const tracks = Array.from(audioTrackList);
        tracks.forEach((track) => {
          track.enabled = track.id === audioTrackId;
        });

        setAudioTrackId(audioTrackId);
      }
    },
    [player]
  );

  useEffect(() => {
    if (audioTracks) {
      const initialId = getInitialAudioTrackId(audioTracks);
      setInitialAudioTrackId(initialId);
    }
  }, [audioTracks]);

  const currentPlayerSource = player.currentSource().src;
  const initialQualityId = useMemo(
    () => getInitialQualityId(playerOptions.sources, currentPlayerSource),
    [playerOptions.sources, currentPlayerSource]
  );

  const handleQualitySelection = useCallback(
    (qualityId: string) => {
      setQuality(player, qualityId, playerOptions, qualities)
        .then(() => {
          setQualityId(qualityId);
        })
        .catch((error) => {
          logger.error('Error setting quality:', error);
        });
    },
    [player, logger, playerOptions, qualities]
  );

  /*
   * -- Current Time --
   * The current time visible in the UI. Updates immediately when the user changes it in the timeline. This time can deviate from the current time in VideoJS.
   * The current time in VideoJS is just updated after the video has been processed for this current timestamp (e.g. after buffering).
   */
  const [currentTime, setCurrentTime] = useState(0);

  /* -- Total Time --*/
  const [totalTime, setTotalTime] = useState(() => sToMs(player.duration()));

  /* -- Volume -- between 0 and 1 */
  const [volume, setVolume] = useState(() => {
    player.volume(settings.volume);
    return settings.volume;
  });

  const handleVolumeChange = useCallback(
    (value: number) => {
      player.volume(value);

      // If volume is not zero, unmute the player.
      player.muted(value <= 0);
    },
    [player]
  );

  const handleVolumeDeltaChange = useCallback(
    (delta: number) => {
      const newVolume = Math.round((player.volume() + delta) * 10) / 10;
      if (newVolume >= 0 && newVolume <= 1) {
        handleVolumeChange(newVolume);
      }
    },
    [handleVolumeChange, player]
  );

  const [isMuted, setIsMuted] = useState(() => {
    player.muted(settings.muted);
    return player.muted();
  });

  const handleMuteChange = useCallback(
    (value: boolean) => {
      player.muted(value);
      setIsMuted(value);

      // When the volume is zero & player is not muted, set the default volume.
      if (!value && player.volume() <= 0) {
        player.volume(settings.volume);
      }
    },
    [player, settings.volume]
  );

  /* -- Chapters -- */
  const chapters = useMemo(() => {
    // Only the first chapter list is supported because the current implementation assumes a single chapter list for simplicity.
    // This may be expanded in the future to support multiple chapter lists if required by the product requirements.
    return playerOptions.chapters ? Object.values(playerOptions.chapters)[0] : [];
  }, [playerOptions.chapters]);

  const onChapterClick = useCallback(
    (chapter: Chapter) => {
      player.currentTime(chapter.start / 1000);
    },
    [player]
  );

  /* -- Fullscreen -- */
  const [isFullscreen, setIsFullscreen] = useState(() => player.isFullscreen());
  const handleFullscreenToggle = useCallback(() => {
    if (player.isFullscreen()) {
      player.exitFullscreen();
    } else {
      player.requestFullscreen();
    }
  }, [player]);

  /* -- Playing -- */
  const [isPlaying, setIsPlaying] = useState(() => !player.paused());
  const [bufferPercentage, setBufferPercentage] = useState(() => player.bufferedPercent());

  /* -- Stopped -- */
  const [isEnded, setIsEnded] = useState(() => player.ended());

  /* -- Looping -- */
  const [isLooping, setIsLooping] = useState(() => {
    player.loop(settings.looping);
    return player.loop();
  });

  /* -- Trimmer -- */
  const [startTimeRef, startTime, setStartTime] = useRefState(0);
  const [endTimeRef, endTime, setEndTime] = useRefState(0);

  const handlePlaybackToggle = useCallback(() => {
    if (player.paused()) {
      tryToPlay(player)
        .then(() => {
          isLiveStream(player.duration()) && player.liveTracker.seekToLiveEdge();
        })
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        .catch((error) => {
          // Player errors are handled by the player error handler already
          logger.error('Playback error:', error);
        });
    } else {
      player.pause();
    }
  }, [player, logger]);

  const handleLoopButtonClick = () => {
    setIsLooping((prevIsLooping) => {
      const newIsLooping = !prevIsLooping;
      player.loop(newIsLooping);
      return newIsLooping;
    });
  };

  /* -- Transcript -- */
  const [transcriptMenuIsOpen, setTranscriptMenuIsOpen] = useState(false);
  const [selectedTranscriptLanguage, setSelectedTranscriptLanguage] = useState<string>('');
  const onTranscriptItemClick = useCallback(
    (cue: TranscriptCue) => {
      player.currentTime(cue.startTime / 1000);
    },
    [player]
  );

  /* -- Subtitles -- */
  const [subtitlesEnabled, setSubtitlesEnabled] = useState(() => settings.subtitleEnabled);
  const [selectedSubtitleId, setSelectedSubtitleId] = useState<string>('');
  const { subtitle, subtitleOptions, transcript, transcriptOptions } = useSubtitles(
    player,
    playerOptions.lng,
    navigator.languages,
    playerOptions?.tracks,
    selectedSubtitleId,
    subtitlesEnabled,
    settings.subtitleFallbackLanguage,
    setSelectedSubtitleId,
    selectedTranscriptLanguage,
    setSelectedTranscriptLanguage
  );
  useEffect(() => {
    if (playerOptions.onTranscript && transcript && transcriptOptions) {
      playerOptions.onTranscript({
        transcript,
        transcriptOptions,
        selectedTranscriptLanguage,
        setSelectedTranscriptLanguage,
        transcriptMenuIsOpen,
        setTranscriptMenuIsOpen,
      });
    }
  }, [playerOptions, transcript, transcriptOptions, selectedTranscriptLanguage, transcriptMenuIsOpen]);

  /* -- Playback Speeds -- */
  const playbackSpeeds = getPlaybackSpeeds(t);
  const [playbackSpeedId, setPlaybackSpeedId] = useState('1');
  const handlePlaybackSpeedSelection = (playbackSpeedId: string) => {
    const playbackRate = Number(playbackSpeedId);
    setPlaybackSpeedId(playbackSpeedId);
    player.playbackRate(playbackRate);
  };

  /* -- Error handling -- */
  const [mediaError, setMediaError] = useState<MediaError | null>(null);

  // Set sources after player UI is ready
  const { sourceIsSet, reloadSource } = useSetSource(
    player,
    playerOptions.sources,
    playerOptions.plugins,
    playerOptions.settings.autoplay,
    playerOptions.settings.preferredQuality
  );

  useTimedMetadata(player);

  useExternalCues(player, playerOptions.externalCues);

  const handleSetSubtitle = useCallback((value: string) => {
    setSelectedSubtitleId(() => {
      const isEnabled = Boolean(value && value !== 'off');
      setSubtitlesEnabled(isEnabled);
      return value;
    });
  }, []);

  const [volumeChangeSupported, setVolumeChangeSupported] = useState(false);
  const [muteChangeSupported, setMuteChangeSupported] = useState(false);

  const { requestCurrentTimeChange, requestCurrentTimeDeltaChange } = usePlayerReady({
    player,
    setTotalTime,
    setStartTime,
    setEndTime,
    setVolume,
    setIsMuted,
    setCurrentTime,
    setIsPlaying,
    setIsEnded,
    setIsFullscreen,
    setBufferPercentage,
    setMediaError,
    setSelectedSubtitleId,
    setVolumeChangeSupported,
    setMuteChangeSupported,
    onFullscreenChange: playerOptions.onFullscreenChange,
    onReady: playerOptions.onReady,
    videoLayout: settings.videoLayout,
    defaultVolume: settings.volume,
    startTimeRef,
    endTimeRef,
  });

  const elementVisibilitySettings = useMemo(() => {
    const visibilitySettings: BreakpointSettings = {
      hideLogo: settings.hideLogo,
      hideTitle: settings.hideTitle,
      hidePlayButton: settings.hidePlayButton,
      hideTimeline: settings.hideTimeline,
      hideVolumeInput: settings.hideVolumeInput || !muteChangeSupported,
      hideVolumeSlider: settings.hideVolumeSlider || !volumeChangeSupported,
      hideTimer: settings.hideTimer,
      hideLoopButton: settings.hideLoopButton,
      hideLanguageMenu: settings.hideLanguageMenu,
      hideMenu: settings.hideMenu,
      hideSettingsMenu: settings.hideSettingsMenu,
      hideFullscreenButton: settings.hideFullscreenButton,
      hidePlaybackSpeed: settings.hidePlaybackSpeed,
      hideBigPlayButton: settings.hideBigPlayButton || !sourceIsSet,
      hideTranscriptButton: settings.hideTranscriptButton,
      hideTranscriptDownload: settings.hideTranscriptDownload,
      stepButtonsAreVisible: settings.stepButtonsAreVisible,
      verticalVolumeSlider: settings.verticalVolumeSlider,
    };

    return visibilitySettings;
  }, [
    settings.hideLogo,
    settings.hideTitle,
    settings.hidePlayButton,
    settings.hideTimeline,
    settings.hideVolumeInput,
    settings.hideVolumeSlider,
    settings.hideTimer,
    settings.hideLoopButton,
    settings.hideLanguageMenu,
    settings.hideMenu,
    settings.hideSettingsMenu,
    settings.hideFullscreenButton,
    settings.hidePlaybackSpeed,
    settings.hideBigPlayButton,
    settings.hideTranscriptButton,
    settings.hideTranscriptDownload,
    settings.stepButtonsAreVisible,
    settings.verticalVolumeSlider,
    muteChangeSupported,
    volumeChangeSupported,
    sourceIsSet,
  ]);

  const isVideoDataLoaded = useVideoDataLoaded(player);

  useMediaSession(player, playerOptions);

  if (mediaError) {
    return <ErrorMessage mediaError={mediaError} onReloadSources={reloadSource} />;
  }

  if (settings.noUI) {
    return null;
  }

  return (
    <UserInterface
      elementVisibilitySettings={elementVisibilitySettings}
      breakpoints={settings.breakpoints}
      isFullscreen={isFullscreen}
      isMobile={isPhone}
      liveUI={settings.liveUI}
      isPlaying={isPlaying}
      isEnded={isEnded}
      isLooping={isLooping}
      audioOnly={settings.audioOnly}
      audioCoverSource={settings.audioCoverSource}
      isTrimmable={settings.trimmable}
      bufferPercentage={bufferPercentage}
      currentTime={currentTime}
      logoUrl={settings.logoUrl}
      playbackSpeedId={playbackSpeedId}
      playbackSpeeds={playbackSpeeds}
      qualities={qualities}
      autoQualityOptionAvailable={isHLSSource(player.currentType())}
      qualityId={qualityId || initialQualityId}
      storyboard={playerOptions.storyboard}
      chapters={chapters}
      downloadSources={playerOptions.downloadSources}
      subtitle={subtitle?.text}
      subtitleOptionId={selectedSubtitleId}
      subtitleOptions={isVideoDataLoaded ? subtitleOptions : []}
      subtitlesEnabled={!!selectedSubtitleId && subtitlesEnabled}
      title={playerOptions.metadata.title}
      totalTime={totalTime}
      trimEndTime={endTime}
      trimStartTime={startTime}
      volume={volume}
      isMuted={isMuted}
      watermark={playerOptions.watermark}
      onChapterClick={onChapterClick}
      onFullscreenToggle={handleFullscreenToggle}
      onPlaybackToggle={handlePlaybackToggle}
      onLoopButtonClick={handleLoopButtonClick}
      onSetPlaybackSpeed={handlePlaybackSpeedSelection}
      onSetSubtitle={handleSetSubtitle}
      onSetQuality={handleQualitySelection}
      onTimeChange={requestCurrentTimeChange}
      onTimeDeltaChange={requestCurrentTimeDeltaChange}
      onTrimEndTimeChange={setEndTime}
      onTrimStartTimeChange={setStartTime}
      onVolumeChange={handleVolumeChange}
      onVolumeDeltaChange={handleVolumeDeltaChange}
      onMuteChange={handleMuteChange}
      onOverlayVisibilityChange={playerOptions.onOverlayVisibilityChange}
      audioTracks={isVideoDataLoaded ? audioTracks : []}
      audioTrackId={audioTrackId || initialAudioTrackId}
      onSetAudioTrack={handleAudioTrackSelection}
      theme={settings.theme}
      controlBarColor={settings.controlBarColor}
      iconColor={settings.iconColor}
      relatedVideos={playerOptions.relatedVideos}
      onRelatedVideoClick={playerOptions.onRelatedVideoClick}
      transcript={transcript}
      selectedTranscriptLanguage={selectedTranscriptLanguage}
      onSelectTranscriptLanguage={setSelectedTranscriptLanguage}
      transcriptLanguages={transcriptOptions}
      onTranscriptItemClick={onTranscriptItemClick}
      onTranscriptButtonClick={playerOptions.onTranscriptButtonClick}
      transcriptMenuIsOpen={transcriptMenuIsOpen}
      setTranscriptMenuIsOpen={setTranscriptMenuIsOpen}
      hideControlsBeforePlay={settings.hideControlsBeforePlay}
    />
  );
}

function isLiveStream(duration: number): boolean {
  return duration === Infinity;
}

function setAdaptiveQuality(player: VideoJsPlayer, id: string) {
  const qualityLevels = Array.from(player.qualityLevels() as QualityLevelList);

  qualityLevels.forEach((quality) => {
    quality.enabled = id === AutoQualityId || `${quality.height}p` === id;
  });

  return Promise.resolve();
}

function setProgressiveQuality(
  player: VideoJsPlayer,
  id: string,
  playerOptionsSources: ProgressiveSources,
  autoSourceId: string,
  autoplay: boolean
): Promise<void> {
  return new Promise((resolve, reject) => {
    const sources = id === AutoQualityId ? playerOptionsSources[autoSourceId] : playerOptionsSources[id];

    // Save the playback state before changing the source
    const currentTime = player.currentTime();
    const playbackRate = player.playbackRate();
    const isPaused = player.paused();

    // Listen for the 'loadedmetadata' event to restore time and playback rate and resume playback if necessary
    // 'loadedmetadata' is the last event on IOS in the 'onLoad' phase.
    player.one(VideoJsNativeEvent.LOADED_METADATA, () => {
      player.currentTime(currentTime);
      player.playbackRate(playbackRate);

      if (isPaused) {
        resolve();
      } else {
        const playPromise = player.play();

        if (playPromise?.then) {
          playPromise.then(resolve, reject);
        } else {
          resolve();
        }
      }
    });

    // Switch sources
    setPlayerSrc(player, sources, autoplay);
  });
}

function setQuality(
  player: VideoJsPlayer,
  qualityId: string,
  playerOptions: PlayerOptions,
  qualities: QualityLevelOption[]
): Promise<void> {
  if (isHLSSource(player.currentType())) {
    return setAdaptiveQuality(player, qualityId);
  } else {
    const progressiveSources = playerOptions.sources.progressive;
    const autoQuality = qualities[0].id;
    const autoplay = playerOptions.settings.autoplay;

    if (!progressiveSources) {
      return Promise.reject(new Error('No progressive sources available'));
    }

    return setProgressiveQuality(player, qualityId, progressiveSources, autoQuality, autoplay);
  }
}

function getInitialAudioTrackId(audioTracks: AudioTrack[]): string {
  const track = audioTracks.find((t) => t.enabled === true);
  return track?.id || '';
}
